/* eslint-disable import/no-default-export */
import classnames from 'classnames';
import React from 'react';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import { connect } from 'react-redux';
import { Dispatch } from 'app/gamma/src/types';
import _ from 'underscore';
import {
  BannerProps,
  TeamBoardsLinkBanner,
} from 'app/scripts/views/board-list/board-list-team-board-link-component';

import Collapsible from 'app/gamma/src/components/collapsible';
import { showLessActiveBoards } from 'app/gamma/src/modules/state/ui/boards-menu';
import {
  BoardStarUpdate,
  updateBoardStarPosition,
} from 'app/gamma/src/modules/state/models/board-stars';
import { MemberContext, MemberInfo } from 'app/gamma/src/modules/context';
import {
  BoardsMenuCategoryModel,
  BoardsMenuCategoryType,
} from 'app/gamma/src/types/models';
import CategoryContent from './category-content';

import styles from './boards-menu.less';

import { Analytics } from '@trello/atlassian-analytics';
import { useFeatureFlag } from '@trello/feature-flag-client';

const icons = {
  [BoardsMenuCategoryType.Starred]: 'star',
  [BoardsMenuCategoryType.Recent]: 'clock',
  [BoardsMenuCategoryType.Personal]: 'board',
  [BoardsMenuCategoryType.Team]: 'organization',
};

import { Noun, Verb, trackUe } from '@trello/analytics';
import { forTemplate } from 'app/common/lib/util/i18n';
import { BoardsMenuButton } from './boards-menu-button';
const format = forTemplate('boards_sidebar_section');
const formatBoardsList = forTemplate('board_list');

interface OwnProps {
  categories: BoardsMenuCategoryModel[];
  hidden: boolean;
  alwaysOpenBoardsMenu: boolean;
  closeBoardsMenu: () => void;
}

interface DispatchProps {
  includeLessActiveBoards: (
    idTeam: string,
    e: React.MouseEvent<HTMLButtonElement>,
  ) => void;

  reorderBoards: (data: BoardStarUpdate) => void;
}

interface AllProps extends OwnProps, DispatchProps {}

interface TeamBoardBannerWrapperProps extends Pick<BannerProps, 'children'> {
  category: BoardsMenuCategoryModel;
  alwaysOpenBoardsMenu: boolean;
  closeBoardsMenu: () => void;
}

const TeamBoardsBannerWrapper = ({
  category,
  alwaysOpenBoardsMenu,
  closeBoardsMenu,
  children,
}: TeamBoardBannerWrapperProps) =>
  category.type === BoardsMenuCategoryType.Team && category.teamId ? (
    <TeamBoardsLinkBanner
      teamName={category.category}
      teamBoardsLink={`/${category.teamShortName}/home`}
      orgId={category.teamId}
      inBoardsMenu
      onBannerLinkClick={!alwaysOpenBoardsMenu ? closeBoardsMenu : _.noop}
      emptyStateBanner={!category.boards.length}
    >
      {children}
    </TeamBoardsLinkBanner>
  ) : (
    <React.Fragment>
      {children({ shouldShowBanner: false, banner: null })}
    </React.Fragment>
  );

const BoardList: React.FunctionComponent<AllProps> = ({
  categories,
  includeLessActiveBoards,
  reorderBoards,
  hidden,
  alwaysOpenBoardsMenu,
  closeBoardsMenu,
}) => {
  const sendGASEvent = useFeatureFlag('dataeng.gasv3-event-tracking', false);

  return (
    <MemberContext.Consumer>
      {(memberInfo: MemberInfo) => {
        return (
          <div className={styles.boards} hidden={hidden}>
            {categories.map((categoryModel) => (
              <TeamBoardsBannerWrapper
                key={categoryModel.id}
                category={categoryModel}
                alwaysOpenBoardsMenu={alwaysOpenBoardsMenu}
                closeBoardsMenu={closeBoardsMenu}
              >
                {({
                  shouldShowBanner: shouldShowTeamBanner,
                  banner: teamBanner,
                }) => {
                  const {
                    boards,
                    category,
                    type,
                    id,
                    numLessActiveBoards = 0,
                    logos,
                    isPremiumTeam,
                    url,
                  } = categoryModel;

                  const isStarredType = type === BoardsMenuCategoryType.Starred;
                  const hasBoards = !!boards.length;

                  if (!isStarredType && !hasBoards && !shouldShowTeamBanner) {
                    return null;
                  }

                  const isTeam = type === BoardsMenuCategoryType.Team;
                  const headerClass = isTeam
                    ? classnames(styles.collapsibleHeader, styles.teamHeader)
                    : styles.collapsibleHeader;
                  const avatarURL = isTeam && !!logos ? logos['30'] : null;
                  const titleClass = classnames(
                    isTeam && styles.teamName,
                    avatarURL && styles.hasAvatar,
                  );
                  const idTeam = isTeam
                    ? id.replace(`${BoardsMenuCategoryType.Team}-`, '')
                    : id;

                  const onDragEnd = (result: DropResult) => {
                    const { draggableId, source, destination } = result;
                    if (
                      // they didn't drop it on a droppable
                      !destination ||
                      // they dropped it in the same position
                      (destination.droppableId === source.droppableId &&
                        destination.index === source.index)
                    ) {
                      return;
                    }

                    if (memberInfo.id) {
                      reorderBoards({
                        idBoard: draggableId,
                        idMember: memberInfo.id,
                        position: destination.index,
                      });
                    }
                  };

                  return (
                    <DragDropContext key={id} onDragEnd={onDragEnd}>
                      <Collapsible
                        key={`category-${id}`}
                        avatarURL={avatarURL}
                        icon={icons[type]}
                        id={idTeam}
                        url={url}
                        categoryId={id}
                        showPremiumIndicator={isPremiumTeam}
                        title={category}
                        open={true}
                        headerClass={headerClass}
                        tabIndex={-1}
                        titleClass={titleClass}
                        toggleClass={styles.collapsibleToggle}
                      >
                        {isStarredType && !hasBoards && (
                          <div className={styles.starredBoardsEmptyState}>
                            {format('star-your-most-important-boards')}
                          </div>
                        )}

                        {shouldShowTeamBanner && (
                          <div className={styles.joinTeamBoardsBanner}>
                            {teamBanner}
                          </div>
                        )}

                        <CategoryContent {...categoryModel} />
                        {numLessActiveBoards > 0 && (
                          <div>
                            <BoardsMenuButton
                              onClick={() => {
                                if (sendGASEvent) {
                                  Analytics.sendUIEvent({
                                    action: 'clicked',
                                    actionSubject: 'link',
                                    actionSubjectId: 'showLessActiveBoardsLink',
                                    source: 'boardsMenuInlineDialog',
                                    containers: {
                                      organization: { id: idTeam },
                                    },
                                  });
                                }

                                trackUe({
                                  category: Noun.BOARDS_MENU,
                                  verb: Verb.SHOWS,
                                  directObj: Noun.LESS_ACTIVE_BOARDS,
                                  context: {
                                    teamID: idTeam,
                                  },
                                });

                                includeLessActiveBoards.bind(null, idTeam);
                              }}
                              className={styles.showLessActive}
                            >
                              {formatBoardsList(
                                'show-less-active-boards-count',
                                {
                                  lessActiveCount: numLessActiveBoards.toString(),
                                },
                              )}
                            </BoardsMenuButton>
                          </div>
                        )}
                      </Collapsible>
                    </DragDropContext>
                  );
                }}
              </TeamBoardsBannerWrapper>
            ))}
          </div>
        );
      }}
    </MemberContext.Consumer>
  );
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return {
    includeLessActiveBoards(idTeam, e) {
      e.preventDefault();
      dispatch(showLessActiveBoards(idTeam));
    },

    reorderBoards(data) {
      dispatch(updateBoardStarPosition(data));
    },
  };
};

export default connect(null, mapDispatchToProps)(BoardList);
